(() => {
  'use strict';

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(event, container) {
    if (event.key !== 'Tab') return;
    const items = Array.from(container.querySelectorAll(focusableSelector)).filter((item) => !item.hidden && item.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function initHeader(header) {
    if (header.dataset.spReady === 'true') return;
    header.dataset.spReady = 'true';
    const menu = header.querySelector('[data-sp-mobile-menu]');
    const menuOpen = header.querySelector('[data-sp-menu-open]');
    const search = header.querySelector('[data-sp-search]');
    const searchOpen = header.querySelector('[data-sp-search-open]');
    const searchInput = header.querySelector('[data-sp-search-input]');
    let returnFocus = null;

    const setOverlay = (overlay, open, trigger) => {
      if (!overlay) return;
      overlay.hidden = !open;
      document.body.classList.toggle('sp-overlay-open', open);
      if (trigger) trigger.setAttribute('aria-expanded', String(open));
      if (open) {
        returnFocus = trigger || document.activeElement;
        window.requestAnimationFrame(() => {
          const target = overlay === search && searchInput ? searchInput : overlay.querySelector(focusableSelector);
          if (target) target.focus();
        });
      } else if (returnFocus && typeof returnFocus.focus === 'function') {
        returnFocus.focus();
      }
    };

    menuOpen?.addEventListener('click', () => setOverlay(menu, true, menuOpen));
    header.querySelectorAll('[data-sp-menu-close]').forEach((button) => button.addEventListener('click', () => setOverlay(menu, false, menuOpen)));
    searchOpen?.addEventListener('click', () => setOverlay(search, true, searchOpen));
    header.querySelectorAll('[data-sp-search-close]').forEach((button) => button.addEventListener('click', () => setOverlay(search, false, searchOpen)));
    header.querySelector('[data-sp-menu-search]')?.addEventListener('click', () => {
      setOverlay(menu, false, menuOpen);
      window.setTimeout(() => setOverlay(search, true, searchOpen), 80);
    });

    header.querySelectorAll('[data-sp-nav-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const group = trigger.closest('.sp-nav__group');
        const willOpen = !group.classList.contains('is-open');
        header.querySelectorAll('.sp-nav__group.is-open').forEach((openGroup) => {
          openGroup.classList.remove('is-open');
          openGroup.querySelector('[data-sp-nav-trigger]')?.setAttribute('aria-expanded', 'false');
        });
        group.classList.toggle('is-open', willOpen);
        trigger.setAttribute('aria-expanded', String(willOpen));
      });
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('.sp-nav__group')) return;
      header.querySelectorAll('.sp-nav__group.is-open').forEach((group) => {
        group.classList.remove('is-open');
        group.querySelector('[data-sp-nav-trigger]')?.setAttribute('aria-expanded', 'false');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (menu && !menu.hidden) setOverlay(menu, false, menuOpen);
        if (search && !search.hidden) setOverlay(search, false, searchOpen);
        header.querySelectorAll('.sp-nav__group.is-open').forEach((group) => {
          group.classList.remove('is-open');
          group.querySelector('[data-sp-nav-trigger]')?.setAttribute('aria-expanded', 'false');
        });
      }
      if (menu && !menu.hidden) trapFocus(event, menu);
      if (search && !search.hidden) trapFocus(event, search);
    });
  }

  function initTestimonials(root) {
    if (root.dataset.spReady === 'true') return;
    root.dataset.spReady = 'true';
    const track = root.querySelector('[data-sp-testimonial-track]');
    const prev = root.querySelector('[data-sp-testimonial-prev]');
    const next = root.querySelector('[data-sp-testimonial-next]');
    if (!track) return;
    const move = (direction) => {
      const card = track.querySelector('.sp-testimonial');
      const gap = parseFloat(getComputedStyle(track).gap) || 20;
      track.scrollBy({ left: direction * ((card?.getBoundingClientRect().width || 320) + gap), behavior: 'smooth' });
    };
    prev?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
  }

  function initQuiz(root) {
    if (root.dataset.spReady === 'true') return;
    root.dataset.spReady = 'true';
    const form = root.querySelector('[data-sp-quiz-form]');
    const steps = Array.from(root.querySelectorAll('[data-sp-quiz-step]'));
    const next = root.querySelector('[data-sp-quiz-next]');
    const back = root.querySelector('[data-sp-quiz-back]');
    const progress = root.querySelector('[data-sp-quiz-progress]');
    const result = root.querySelector('[data-sp-quiz-result]');
    const reset = root.querySelector('[data-sp-quiz-reset]');
    if (!form || !steps.length || !next || !result) return;
    let index = 0;

    const showStep = () => {
      steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== index;
        step.classList.toggle('is-active', stepIndex === index);
      });
      back.hidden = index === 0;
      next.textContent = index === steps.length - 1 ? 'Show my edit' : 'Continue';
      progress.style.width = `${((index + 1) / steps.length) * 100}%`;
    };

    const activeValue = (name) => form.querySelector(`input[name="${name}"]:checked`)?.value || '';
    const showResult = () => {
      const occasion = activeValue('occasion');
      const vibe = activeValue('vibe');
      const priority = activeValue('priority');
      let results = {};
      try { results = JSON.parse(root.dataset.results || '{}'); } catch (error) { results = {}; }
      const match = results[occasion] || {};
      form.hidden = true;
      root.querySelector('.sp-quiz__progress').hidden = true;
      result.hidden = false;
      root.querySelector('[data-sp-quiz-title]').textContent = match.title || 'Your Saint Parsons edit';
      root.querySelector('[data-sp-quiz-text]').textContent = match.text || 'A curated collection matched to your plans.';
      const vibeLabels = { bold: 'bold statement pieces', romantic: 'romantic details', easy: 'effortless silhouettes', trend: 'trend-forward styling' };
      const priorityLabels = { fit: 'a flattering fit', comfort: 'all-day comfort', impact: 'maximum impact', value: 'versatile value' };
      root.querySelector('[data-sp-quiz-personalized]').textContent = `We prioritized ${vibeLabels[vibe] || 'your preferred style'} and ${priorityLabels[priority] || 'the details that matter to you'}.`;
      const link = root.querySelector('[data-sp-quiz-link]');
      if (match.url) link.href = match.url;
      result.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    next.addEventListener('click', () => {
      const selected = steps[index].querySelector('input:checked');
      if (!selected) {
        const firstInput = steps[index].querySelector('input');
        firstInput?.focus();
        steps[index].classList.add('has-error');
        return;
      }
      steps[index].classList.remove('has-error');
      if (index === steps.length - 1) showResult();
      else { index += 1; showStep(); }
    });
    back?.addEventListener('click', () => { if (index > 0) { index -= 1; showStep(); } });
    reset?.addEventListener('click', () => {
      form.reset();
      index = 0;
      result.hidden = true;
      form.hidden = false;
      root.querySelector('.sp-quiz__progress').hidden = false;
      showStep();
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    showStep();
  }

  function initSizeGuide(root) {
    if (root.dataset.spReady === 'true') return;
    root.dataset.spReady = 'true';
    const tabs = Array.from(root.querySelectorAll('[data-sp-size-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-sp-size-panel]'));
    const unitToggle = root.querySelector('[data-sp-unit-toggle]');
    tabs.forEach((tab) => tab.addEventListener('click', () => {
      tabs.forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
      panels.forEach((panel) => { panel.hidden = panel.dataset.spSizePanel !== tab.dataset.spSizeTab; });
    }));
    unitToggle?.addEventListener('click', () => {
      const showCm = unitToggle.getAttribute('aria-pressed') !== 'true';
      unitToggle.setAttribute('aria-pressed', String(showCm));
      unitToggle.textContent = showCm ? 'Show inches' : 'Show centimeters';
      root.querySelectorAll('[data-in]').forEach((cell) => {
        const inches = cell.dataset.in;
        cell.textContent = showCm
          ? inches.split('–').map((value) => Math.round(Number(value) * 2.54)).join('–')
          : inches;
      });
      root.querySelectorAll('table caption').forEach((caption) => {
        caption.textContent = caption.textContent.replace(/ \((in|cm)\)$/, '') + ` (${showCm ? 'cm' : 'in'})`;
      });
    });
  }

  function initRecentlyViewed(root) {
    if (root.dataset.spReady === 'true') return;
    root.dataset.spReady = 'true';
    const storageKey = 'spRecentlyViewed';
    const grid = root.querySelector('[data-sp-recent-grid]');
    let products = [];
    try { products = JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch (error) { products = []; }
    const currentData = root.querySelector('[data-sp-current-product]');
    if (currentData) {
      try {
        const current = JSON.parse(currentData.textContent);
        products = [current, ...products.filter((item) => item.handle !== current.handle)].slice(0, 8);
        localStorage.setItem(storageKey, JSON.stringify(products));
      } catch (error) { /* Storage is an enhancement only. */ }
    }
    const visible = products.filter((item) => item.handle !== root.dataset.currentHandle).slice(0, 4);
    if (!grid || !visible.length) return;
    visible.forEach((item) => {
      const card = document.createElement('article');
      card.className = 'sp-recent-card';
      const link = document.createElement('a');
      link.href = item.url;
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      const title = document.createElement('h3');
      title.textContent = item.title;
      const price = document.createElement('p');
      price.textContent = item.price;
      link.append(image, title, price);
      card.append(link);
      grid.append(card);
    });
    root.hidden = false;
  }

  function initStickyAdd(root) {
    if (root.dataset.spReady === 'true') return;
    root.dataset.spReady = 'true';
    const mainButton = document.querySelector('.product-form__submit');
    const stickyButton = root.querySelector('[data-sp-sticky-add]');
    const priceTarget = root.querySelector('[data-sp-sticky-price]');
    if (!mainButton || !stickyButton) return;
    root.hidden = false;
    const observer = new IntersectionObserver(([entry]) => root.classList.toggle('is-visible', !entry.isIntersecting), { threshold: 0 });
    observer.observe(mainButton);
    stickyButton.addEventListener('click', () => mainButton.click());
    const productInfo = document.querySelector('.product__info-container');
    if (productInfo && priceTarget) {
      const sync = () => {
        const livePrice = productInfo.querySelector('[id^="price-"] .price-item--regular, [id^="price-"] .price-item--sale');
        if (livePrice) priceTarget.textContent = livePrice.textContent.trim();
        stickyButton.disabled = mainButton.disabled;
        stickyButton.textContent = mainButton.textContent.trim();
      };
      new MutationObserver(sync).observe(productInfo, { childList: true, subtree: true, characterData: true, attributes: true });
      sync();
    }
  }

  function initLegacyCarousel(root) {
    if (root.dataset.spReady === 'true') return;
    root.dataset.spReady = 'true';
    const track = root.querySelector('[data-sp-carousel-track]');
    const slides = Array.from(root.querySelectorAll('[data-sp-carousel-slide]'));
    const dotsWrap = root.querySelector('[data-sp-carousel-dots]');
    if (!track || slides.length < 2) return;
    let index = 0;
    let timer;
    const setIndex = (next) => {
      index = (next + slides.length) % slides.length;
      track.style.transform = `translateX(-${index * 100}%)`;
      dotsWrap?.querySelectorAll('button').forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === index)));
    };
    if (dotsWrap) {
      slides.forEach((slide, slideIndex) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sp-carousel-dot';
        dot.setAttribute('aria-label', `Show slide ${slideIndex + 1}`);
        dot.addEventListener('click', () => setIndex(slideIndex));
        dotsWrap.append(dot);
      });
    }
    const start = () => {
      if (root.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      window.clearInterval(timer);
      timer = window.setInterval(() => setIndex(index + 1), Number(root.dataset.interval) || 6000);
    };
    root.addEventListener('mouseenter', () => window.clearInterval(timer));
    root.addEventListener('mouseleave', start);
    setIndex(0);
    start();
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-sp-header]').forEach(initHeader);
    scope.querySelectorAll('[data-sp-testimonials]').forEach(initTestimonials);
    scope.querySelectorAll('[data-sp-style-quiz]').forEach(initQuiz);
    scope.querySelectorAll('[data-sp-size-guide]').forEach(initSizeGuide);
    scope.querySelectorAll('[data-sp-recently-viewed]').forEach(initRecentlyViewed);
    scope.querySelectorAll('[data-sp-sticky-atc]').forEach(initStickyAdd);
    scope.querySelectorAll('[data-sp-carousel]').forEach(initLegacyCarousel);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
